import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DateTabItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/DateTabItem';
import BrandWrapSection2 from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/BrandWrapSection2';
import BrandMainSection2 from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/BrandMainSection2';
import BrandSubSection2 from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/BrandSubSection2';
import BrandSquareTabItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/BrandSquareTabItem';
import MenuBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/MenuBarItem';
import Menu2LineBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/Menu2LineBarItem';
import useRenderProducts from '@hooks/useRenderProducts';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import {
  PromotionBrandModule,
  PromotionBrandSchedule,
  PromotionBrandCouponInfo,
  PromotionSubBrand,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import { useServerTimeActions } from '@store/serverTimeStore';
import useLinkTo from '@hooks/useLinkTo';
import { useCouponDownload } from '@hooks/useCouponDownload';
import useGa4Event from '@hooks/useGa4Event';
import dayjs from '@wconcept/wconcept-common/libs/dayjs';
import PrdItemSkeleton from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/PrdItemSkeleton';
import { usePromotionTabProducts } from '@hooks/usePromotionTabProducts';
import useMolocoLogger from '@hooks/useMolocoLogger';
import useBrazeLogger from '@hooks/useBrazeLogger';
import { SwiperClass } from 'swiper/react';
import numberToKorean from '@wconcept/wconcept-common/utils/numberToKorean';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import { isInDisplayTimeRange } from '../utils/displayTimeChecker';

/** 서브 브랜드 최대 노출 개수 */
const MAX_SUB_BRAND_COUNT = 30;
/** 서브 '영역' 최소 요건 — 유효 서브 브랜드 3개 미만이면 서브 영역 비전시 */
const MIN_SUB_BRAND_COUNT = 3;
/** 서브 브랜드별 상품 하한 — 3개 미만이면 해당 서브 브랜드 비전시 */
const MIN_SUB_BRAND_PRODUCT_COUNT = 3;
/** 메인 브랜드별 상품 6개 미만이면 해당 메인 브랜드 비전시 */
const MIN_MAIN_BRAND_PRODUCT_COUNT = 6;

interface BrandModuleProps {
  brandModules: PromotionBrandModule;
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  subTitle: string;
  title: string;
  promotionId?: number;
  promotionTitle?: string;
  contentId?: string;
  isVisible?: boolean;
}

function BrandModule({
  brandModules,
  bgType,
  visualTokens,
  isDesktop,
  subTitle,
  title,
  promotionId,
  promotionTitle,
  contentId,
  isVisible,
}: BrandModuleProps) {
  const {
    customColorsFull,
    isDarkSurface,
    productUnitColorType: promotionProductUnitColorType,
    productSkeletonBgType: promotionProductSkeletonBgType,
  } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });

  const surfacePreset = isDarkSurface ? 'black' : 'white';
  const sectionColorType = customColorsFull ?? surfacePreset;

  const {
    sendGa4SelectItem,
    sendGa4TabClick,
    sendGa4CouponClick,
    sendGa4AddToWishProduct,
    sendGa4ButtonClick,
  } = useGa4Event();

  const { downloadSingleCoupon } = useCouponDownload();
  const { linkTo } = useLinkTo();
  const { sendMolocoAddToWish } = useMolocoLogger();
  const { sendBrazeRenewalMyHeart } = useBrazeLogger();

  const type = useMemo(() => brandModules.type, [brandModules.type]);
  const periodType = useMemo(
    () => brandModules.periodType,
    [brandModules.periodType]
  );
  const getInitialSchedule = useCallback(
    (shuffle = false) => {
      const shuffleSubBrands = (
        list: PromotionSubBrand[]
      ): PromotionSubBrand[] =>
        shuffle ? [...list].sort(() => Math.random() - 0.5) : list;

      const getMainBrands = (currentSchedule: PromotionBrandSchedule) => {
        const validMains = (currentSchedule.mainBrands ?? []).filter(
          mainBrand =>
            (mainBrand.productCdList?.length ?? 0) >=
            MIN_MAIN_BRAND_PRODUCT_COUNT
        );
        return shuffle && currentSchedule.randomDisplayEnabled
          ? [...validMains].sort(() => Math.random() - 0.5)
          : validMains;
      };
      const hasValidMainBrand = (currentSchedule: PromotionBrandSchedule) =>
        (currentSchedule.mainBrands ?? []).some(
          mainBrand =>
            (mainBrand.productCdList?.length ?? 0) >=
            MIN_MAIN_BRAND_PRODUCT_COUNT
        );

      let newSchedule: PromotionBrandSchedule[] = [];
      if (type === 'MAIN_SUB') {
        newSchedule = brandModules.schedules
          .filter(
            currentSchedule =>
              hasValidMainBrand(currentSchedule) &&
              currentSchedule.subBrands.filter(
                subBrand =>
                  subBrand.productCdList?.length >= MIN_SUB_BRAND_PRODUCT_COUNT
              ).length >= MIN_SUB_BRAND_COUNT
          )
          .map((currentSchedule: PromotionBrandSchedule) => ({
            ...currentSchedule,
            mainBrands: getMainBrands(currentSchedule),
            subBrands:
              currentSchedule.subBrands.filter(
                subBrand =>
                  subBrand.productCdList?.length >= MIN_SUB_BRAND_PRODUCT_COUNT
              ).length >= MIN_SUB_BRAND_COUNT
                ? shuffleSubBrands(
                    currentSchedule.subBrands.filter(
                      subBrand =>
                        subBrand.productCdList?.length >=
                        MIN_SUB_BRAND_PRODUCT_COUNT
                    )
                  ).slice(0, MAX_SUB_BRAND_COUNT)
                : [],
          }));
      } else if (type === 'MAIN') {
        newSchedule = brandModules.schedules
          .filter(currentSchedule => hasValidMainBrand(currentSchedule))
          .map((currentSchedule: PromotionBrandSchedule) => ({
            ...currentSchedule,
            mainBrands: getMainBrands(currentSchedule),
            subBrands:
              currentSchedule.subBrands.filter(
                subBrand =>
                  subBrand.productCdList?.length >= MIN_SUB_BRAND_PRODUCT_COUNT
              ).length >= MIN_SUB_BRAND_COUNT
                ? shuffleSubBrands(
                    currentSchedule.subBrands.filter(
                      subBrand =>
                        subBrand.productCdList?.length >=
                        MIN_SUB_BRAND_PRODUCT_COUNT
                    )
                  ).slice(0, MAX_SUB_BRAND_COUNT)
                : [],
          }));
      } else if (type === 'SUB') {
        newSchedule = brandModules.schedules
          .filter(
            currentSchedule =>
              currentSchedule.subBrands?.length >= MIN_SUB_BRAND_COUNT
          )
          .map((currentSchedule: PromotionBrandSchedule) => ({
            ...currentSchedule,
            subBrands:
              currentSchedule.subBrands.length >= MIN_SUB_BRAND_COUNT
                ? shuffleSubBrands(currentSchedule.subBrands)
                : [],
          }));
      }
      return newSchedule;
    },
    [brandModules.schedules, type]
  );

  const [schedule, setSchedule] = useState<PromotionBrandSchedule[]>(() =>
    getInitialSchedule(false)
  );

  const getMainBrandTab = useCallback(
    () =>
      schedule?.find(s =>
        isInDisplayTimeRange(s.displayStartDate, s.displayEndDate)
      ),
    [schedule]
  );
  const [brandMainTab, setBrandMainTab] = useState<PromotionBrandSchedule>(
    getMainBrandTab() || schedule?.[0]
  );

  const [brandSubTab, setBrandSubTab] = useState<PromotionSubBrand>(
    schedule?.[0]?.subBrands?.[0]
  );

  // 날짜 탭 변경 시 첫 번째 브랜드로 리셋
  const [mainBrandTabCd, setMainBrandTabCd] = useState<string | undefined>(
    undefined
  );
  const activeMainBrand = useMemo(() => {
    const mains = brandMainTab?.mainBrands ?? [];
    return mains.find(b => b.brandCd === mainBrandTabCd) ?? mains[0];
  }, [brandMainTab, mainBrandTabCd]);

  const hasShuffledRef = useRef(false);
  const [isShuffleReady, setIsShuffleReady] = useState(false);

  useLayoutEffect(() => {
    if (hasShuffledRef.current) return;
    hasShuffledRef.current = true;

    const newSchedule = getInitialSchedule(true);
    setSchedule(newSchedule);
    const mainTab =
      newSchedule?.find(s =>
        isInDisplayTimeRange(s.displayStartDate, s.displayEndDate)
      ) ?? newSchedule?.[0];
    if (mainTab) {
      setBrandMainTab(mainTab);
      setBrandSubTab(mainTab.subBrands?.[0]);
    }
    setIsShuffleReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pageNo 공통 규칙: 기간유형 ALL이면 미전송, 아니면 일자탭 순서(index + 1)
  const toDateTabPageNo = useCallback(
    (index: number) =>
      periodType !== 'ALL' && index >= 0 ? index + 1 : undefined,
    [periodType]
  );

  // 현재 선택된 일자탭 기준 pageNo (탭 클릭 외 모든 이벤트에서 사용)
  const dateTabPageNo = useMemo(
    () =>
      toDateTabPageNo(
        schedule.findIndex(s => s.scheduleId === brandMainTab?.scheduleId)
      ),
    [toDateTabPageNo, schedule, brandMainTab?.scheduleId]
  );

  const brandMainRef = useRef<SwiperClass | null>(null);
  const brandSubRef = useRef<SwiperClass | null>(null);
  const { getAdjustedTime } = useServerTimeActions();

  // 메인 브랜드 상품 조회 - 날짜 탭 변경 시 해당 스케줄의 메인 브랜드만 fetch
  const { productsMap: mainBrandProductsMap, isSuccess: isMainBrandSuccess } =
    usePromotionTabProducts({
      contentId: contentId || '',
      tabKey: `main-${brandMainTab?.scheduleId ?? ''}-${
        activeMainBrand?.brandCd ?? ''
      }`,
      itemCodes: activeMainBrand?.productCdList || [],
      enabled: isVisible && !!activeMainBrand,
    });

  // 서브 브랜드 상품 조회 - 서브 브랜드 탭 클릭 시 해당 브랜드만 fetch
  const { productsMap: subBrandProductsMap, isSuccess: isSubBrandSuccess } =
    usePromotionTabProducts({
      contentId: contentId || '',
      tabKey: `sub-${brandMainTab?.scheduleId ?? ''}-${
        brandSubTab?.brandCd ?? ''
      }`,
      itemCodes: brandSubTab?.productCdList || [],
      enabled: isVisible && !!brandSubTab,
    });

  useEffect(() => {
    setMainBrandTabCd(undefined);
    if (brandMainTab?.subBrands?.length) {
      setBrandSubTab(brandMainTab?.subBrands?.[0]);
    }
  }, [brandMainTab]);

  const { renderProducts: renderBrandMainProducts } = useRenderProducts({
    doAfterAddMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: true,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        page: 'promotion',
        area: 'promotion_mainbrand_myheart',
        issueCd: promotionId?.toString(),
        tabNm: activeMainBrand?.brandNameKr,
        issueNm: promotionTitle,
        pageNo: dateTabPageNo,
        variable: title,
      });
      sendMolocoAddToWish({
        itemCd: product.itemCd,
        amount: product.finalPrice,
        pageId: window.location.pathname,
      });
      sendBrazeRenewalMyHeart({
        product,
        pageId: window.location.pathname,
        useGender: true,
        defaultGender: 'women',
      });
      // onClickHeart?.(product, true, index || 0);
    },
    doAfterDeleteMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: false,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        page: 'promotion',
        area: 'promotion_mainbrand_myheart',
        tabNm: activeMainBrand?.brandNameKr,
        issueCd: promotionId?.toString(),
        issueNm: promotionTitle,
        pageNo: dateTabPageNo,
        variable: title,
      });
      // onClickHeart?.(product, false, index || 0);
    },
  });
  const { renderProducts: renderBrandSubProducts } = useRenderProducts({
    doAfterAddMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: true,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        page: 'promotion',
        area: 'promotion_subbrand_myheart',
        issueCd: promotionId?.toString(),
        tabNm: brandSubTab?.brandNameKr,
        issueNm: promotionTitle,
        pageNo: dateTabPageNo,
        variable: title,
      });
      sendMolocoAddToWish({
        itemCd: product.itemCd,
        amount: product.finalPrice,
        pageId: window.location.pathname,
      });
      sendBrazeRenewalMyHeart({
        product,
        pageId: window.location.pathname,
        useGender: true,
        defaultGender: 'women',
      });
      // onClickHeart?.(product, true, index || 0);
    },
    doAfterDeleteMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: false,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        page: 'promotion',
        area: 'promotion_subbrand_myheart',
        tabNm: brandSubTab?.brandNameKr,
        issueCd: promotionId?.toString(),
        issueNm: promotionTitle,
        pageNo: dateTabPageNo,
        variable: title,
      });
      // onClickHeart?.(product, false, index || 0);
    },
  });

  const getBrandMainProducts = useCallback(() => {
    if (!isDesktop && !isMainBrandSuccess) {
      return Array.from({
        length: activeMainBrand?.productCdList?.length || 0,
      }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <PrdItemSkeleton key={index} bgType={promotionProductSkeletonBgType} />
      ));
    }

    if (
      !activeMainBrand ||
      (activeMainBrand.productCdList?.length ?? 0) <
        MIN_MAIN_BRAND_PRODUCT_COUNT
    ) {
      return null;
    }

    const products = activeMainBrand.productCdList
      .map(itemCd => mainBrandProductsMap.get(itemCd))
      .filter((p): p is Product => !!p);

    const visibleProducts =
      !isDesktop && products.length >= 6
        ? products.slice(0, Math.floor(products.length / 2) * 2)
        : products;

    return renderBrandMainProducts({
      type: 'simple',
      products: visibleProducts,
      maxCount: 30,
      minCount: MIN_MAIN_BRAND_PRODUCT_COUNT,
      isBrandNameKr: true,
      rs: isDesktop ? '417' : '216',
      colorType: promotionProductUnitColorType,
      onClickProduct: (product, index) => {
        sendGa4SelectItem({
          product,
          area: 'promotion_mainbrand_item',
          page: 'promotion',
          ecommercePath: '프로모션',
          tabNm: activeMainBrand?.brandNameKr,
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          pageNo: dateTabPageNo,
          variable: title,
          listNo: Number(index + 1),
        });
      },
    });
  }, [
    dateTabPageNo,
    isMainBrandSuccess,
    mainBrandProductsMap,
    activeMainBrand,
    renderBrandMainProducts,
    isDesktop,
    promotionProductSkeletonBgType,
    promotionProductUnitColorType,
    sendGa4SelectItem,
    promotionId,
    promotionTitle,
    title,
  ]);

  const getDesc = useCallback((couponInfo: PromotionBrandCouponInfo) => {
    if (couponInfo?.maxDiscountAmount) {
      return `최대 ${numberToKorean(
        couponInfo.maxDiscountAmount,
        'mixed'
      )}원 할인`;
    }
    return '';
  }, []);

  // 브랜드 메인 렌더
  const getBrandMainRender = () => (
    <BrandMainSection2
      keyVisualImgRs={isDesktop ? undefined : '670'}
      brandName={activeMainBrand?.brandNameKr ?? ''}
      brandNameEn={activeMainBrand?.brandName}
      keyVisualImgSrc={activeMainBrand?.imageUrl}
      keyVisualTitle={activeMainBrand?.mainText}
      keyVisualSubTitle={activeMainBrand?.subText}
      onClickBrandBanner={() => {
        linkTo.push({
          url: activeMainBrand?.landingUrl,
        });
      }}
      couponTitle={
        activeMainBrand?.couponDisplayStatus === 'USE'
          ? activeMainBrand?.brandCouponInfo?.couponName
          : undefined
      }
      couponDesc={
        activeMainBrand?.couponDisplayStatus === 'USE'
          ? getDesc(activeMainBrand?.brandCouponInfo)
          : undefined
      }
      onClickCoupon={() => {
        sendGa4CouponClick({
          area: 'promotion_mainbrand_coupon_download',
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          pageNo: dateTabPageNo,
          page: 'promotion',
          tabNm: activeMainBrand?.brandNameKr,
          listNo: brandMainTab.displayOrder,
          text: activeMainBrand?.brandCouponInfo?.couponName,
          couponCd: activeMainBrand?.brandCouponInfo?.couponCd,
          couponNm: activeMainBrand?.brandCouponInfo?.couponName,
          variable: title,
        });
        downloadSingleCoupon(activeMainBrand?.brandCouponInfo?.couponCd);
      }}
      productList={getBrandMainProducts()}
      onClickMore={() => {
        sendGa4ButtonClick({
          area: 'promotion_mainbrand_button',
          page: 'promotion',
          bannerType: activeMainBrand?.landingUrl?.includes('/brand/')
            ? 'brand'
            : 'issue',
          tabNm: activeMainBrand?.brandNameKr,
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          pageNo: dateTabPageNo,
          variable: title,
          text: '자세히 보기',
        });
        linkTo.push({
          url: activeMainBrand?.landingUrl,
        });
      }}
      isDesktop={isDesktop}
      btnText="자세히 보기"
      brandMainRef={brandMainRef}
      brandCd={activeMainBrand?.brandCd?.toString()}
      colorType={sectionColorType}
    />
  );

  const getSubBrandList = useCallback(() => {
    // 3개 이상의 상품이 있는 브랜드만 필터링
    const validSubBrands = brandMainTab?.subBrands?.filter(
      brand =>
        brand.productCdList &&
        brand.productCdList.length >= MIN_SUB_BRAND_PRODUCT_COUNT
    );
    if (validSubBrands?.length < MIN_SUB_BRAND_COUNT) {
      return undefined;
    }

    return validSubBrands?.map((subBrand, index) => (
      <BrandSquareTabItem
        key={subBrand.brandCd}
        brandName={subBrand.brandNameKr}
        imgSrc={subBrand.brandImageUrl}
        rs="250"
        value={subBrand.brandCd?.toString()}
        selectedValue={brandSubTab?.brandCd?.toString()}
        colorType={sectionColorType}
        onClick={() => {
          brandSubRef.current?.slideTo(0, 0);
          sendGa4TabClick({
            area: 'promotion_subbrand_tab',
            page: 'promotion',
            issueCd: promotionId?.toString(),
            issueNm: promotionTitle,
            pageNo: dateTabPageNo,
            text: subBrand.brandNameKr,
            variable: title,
            listNo: index + 1,
          });
          setBrandSubTab(subBrand);
        }}
      />
    ));
  }, [
    dateTabPageNo,
    title,
    brandMainTab,
    brandSubTab,
    sectionColorType,
    sendGa4TabClick,
    promotionId,
    promotionTitle,
    brandSubRef,
    setBrandSubTab,
  ]);

  const getBrandSubProducts = useCallback(() => {
    if (!isDesktop && !isSubBrandSuccess) {
      return Array.from({
        length: brandSubTab?.productCdList?.length || 0,
      }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <PrdItemSkeleton key={index} bgType={promotionProductSkeletonBgType} />
      ));
    }

    const validSubBrands = brandMainTab?.subBrands?.filter(
      brand =>
        brand.productCdList &&
        brand.productCdList.length >= MIN_SUB_BRAND_PRODUCT_COUNT
    );

    if (validSubBrands?.length < MIN_SUB_BRAND_COUNT) {
      return undefined;
    }

    return renderBrandSubProducts({
      type: 'simple',
      products: brandSubTab
        ? brandSubTab?.productCdList
            ?.map(itemCd => subBrandProductsMap.get(itemCd))
            .filter((p): p is Product => !!p)
        : [],
      maxCount: 30,
      isBrandNameKr: true,
      colorType: promotionProductUnitColorType,
      onClickProduct: (product, index) => {
        sendGa4SelectItem({
          product,
          area: 'promotion_subbrand_item',
          page: 'promotion',
          ecommercePath: '프로모션',
          issueCd: promotionId?.toString(),
          tabNm: brandSubTab?.brandNameKr,
          issueNm: promotionTitle,
          pageNo: dateTabPageNo,
          variable: title,
          listNo: Number(index + 1),
        });
      },
    });
  }, [
    dateTabPageNo,
    title,
    isDesktop,
    isSubBrandSuccess,
    subBrandProductsMap,
    brandMainTab?.subBrands,
    renderBrandSubProducts,
    brandSubTab,
    promotionProductSkeletonBgType,
    promotionProductUnitColorType,
    sendGa4SelectItem,
    promotionId,
    promotionTitle,
  ]);

  // 브랜드 서브 렌더
  const getBrandSubRender = () => {
    // 유효한 서브 브랜드가 없으면 렌더링하지 않음
    if (!brandSubTab || !brandSubTab?.productCdList?.length) {
      return undefined;
    }

    return (
      <BrandSubSection2
        brandSubRef={brandSubRef}
        keyVisualImgSrc={brandSubTab?.detailImageUrl || undefined}
        keyVisualImgRs={isDesktop ? undefined : '670'}
        couponTitle={
          brandSubTab?.couponDisplayStatus === 'USE'
            ? brandSubTab?.brandCouponInfo?.couponName
            : undefined
        }
        couponDesc={
          brandSubTab?.couponDisplayStatus === 'USE'
            ? getDesc(brandSubTab?.brandCouponInfo)
            : undefined
        }
        onClickCoupon={() => {
          sendGa4CouponClick({
            area: 'promotion_subbrand_coupon_download',
            issueCd: promotionId?.toString(),
            issueNm: promotionTitle,
            pageNo: dateTabPageNo,
            tabNm: brandSubTab?.brandNameKr,
            couponCd: brandSubTab?.brandCouponInfo?.couponCd,
            couponNm: brandSubTab?.brandCouponInfo?.couponName,
            text: brandSubTab?.brandCouponInfo?.couponName,
            variable: title,
          });
          downloadSingleCoupon(brandSubTab?.brandCouponInfo?.couponCd);
        }}
        subBrandList={getSubBrandList()}
        isSubBrandTabReady={isShuffleReady}
        selectedTabId={brandSubTab?.brandCd?.toString()}
        scrollResetKey={brandMainTab?.scheduleId}
        productList={getBrandSubProducts()}
        onClickMore={() => {
          sendGa4ButtonClick({
            area: 'promotion_subbrand_button',
            issueCd: promotionId?.toString(),
            pageNo: dateTabPageNo,
            tabNm: brandSubTab?.brandNameKr,
            variable: title,
            bannerType: brandSubTab?.landingUrl?.includes('/brand/')
              ? 'brand'
              : 'issue',
            text: '자세히 보기',
          });

          linkTo.push({
            url: brandSubTab?.landingUrl,
          });
        }}
        isDesktop={isDesktop}
        btnText="자세히 보기"
        colorType={sectionColorType}
      />
    );
  };

  const getMainBrandTabList = useCallback(() => {
    const mains = brandMainTab?.mainBrands ?? [];
    if (!(type === 'MAIN' || type === 'MAIN_SUB') || mains.length === 0) {
      return undefined;
    }
    return mains.map((mainBrand, index) => {
      const handleClickMainBrandTab = () => {
        brandMainRef.current?.slideTo(0, 0);
        sendGa4TabClick({
          area: 'promotion_mainbrand_tab',
          page: 'promotion',
          issueCd: promotionId?.toString(),
          issueNm: promotionTitle,
          pageNo: dateTabPageNo,
          text: mainBrand.brandNameKr,
          variable: title,
          listNo: index + 1,
        });
        setMainBrandTabCd(mainBrand.brandCd?.toString());
      };
      return isDesktop ? (
        <Menu2LineBarItem
          key={mainBrand.brandCd}
          value={mainBrand.brandCd?.toString()}
          type="type2"
          text={mainBrand.brandNameKr}
          selectedValue={activeMainBrand?.brandCd?.toString()}
          onClick={handleClickMainBrandTab}
        />
      ) : (
        <MenuBarItem
          key={mainBrand.brandCd}
          value={mainBrand.brandCd?.toString()}
          text={mainBrand.brandNameKr}
          onClick={handleClickMainBrandTab}
        />
      );
    });
  }, [
    dateTabPageNo,
    brandMainTab,
    type,
    isDesktop,
    activeMainBrand,
    sendGa4TabClick,
    promotionId,
    promotionTitle,
    title,
  ]);

  // 탭 메뉴 리스트
  const getTabMenuList = useCallback(
    () =>
      !brandModules.periodType.includes('ALL')
        ? schedule?.map((currentSchedule, index) => (
            <DateTabItem
              key={currentSchedule.scheduleId}
              serverTime={getAdjustedTime()}
              startDate={currentSchedule.displayStartDate}
              endDate={currentSchedule.displayEndDate}
              value={currentSchedule.scheduleId.toString()}
              selectedValue={brandMainTab?.scheduleId.toString()}
              type="time"
              colorType={sectionColorType}
              onClick={() => {
                brandMainRef.current?.slideTo(0, 0);
                brandSubRef.current?.slideTo(0, 0);
                sendGa4TabClick({
                  area: 'promotion_brand_tab',
                  page: 'promotion',
                  issueCd: promotionId?.toString(),
                  issueNm: promotionTitle,
                  pageNo: toDateTabPageNo(index),
                  text: dayjs(currentSchedule.displayStartDate).format(
                    'MM.DD(ddd)'
                  ),
                  variable: title,
                  listNo: index + 1,
                });

                setBrandMainTab(currentSchedule);
                setBrandSubTab(currentSchedule.subBrands?.[0]);
              }}
            />
          ))
        : undefined,
    [
      brandModules.periodType,
      schedule,
      getAdjustedTime,
      brandMainTab?.scheduleId,
      sectionColorType,
      sendGa4TabClick,
      promotionId,
      promotionTitle,
      title,
      toDateTabPageNo,
    ]
  );
  // 1. 브랜드 비전시 조건(공통)
  // 해당 브랜드의 전시 가능 상품이 3개 미만이면 브랜드 비전시
  // 3개 이상이면 통과, 최대 30개까지만 노출
  // 2. 서브 브랜드 영역 최소 전시 요건
  // 유효 브랜드가 3개 이상일 때만 서브 영역 전시
  // 3개 미만이면, 서브 브랜드 영역 전체 비전시
  // 3. 일자 탭 비전시 조건
  // 메인 or 서브 : 해당 날짜에 메인 브랜드 혹은 서브 브랜드 영역이 최소 전시 요건을 만족하지 못 하는 경우, 일자 탭 비전시
  // 메인&서브 : 해당 날짜에 메인 영역, 서브 영역 중 하나라도 최소 전시 요건을 만족하지 못 하는 경우, 일자 탭 비전시
  // 4. 모듈 비전시 조건
  // 유효한(브랜드가 전시 가능한) 일자 탭이 2개 이상인 경우부터 모듈 전시
  // 2개 미만인 경우, 모듈 자체 비전시
  // - 기간 유형 = 전체인 경우, 1~2번만 체크하여 브랜드, 모듈 노출 여부 판단
  // - 기간 유형 = 일별, 기간별인 경우, 1~4번 모두 체크하여 브랜드, 일자 탭 및 모듈 노출 여부 판단

  if (periodType !== 'ALL' && schedule?.length < 2) {
    return null;
  }
  if (!brandMainTab && type === 'MAIN') {
    return null;
  }

  if (!brandSubTab && type === 'SUB') {
    return null;
  }

  if (
    (!brandMainTab && !brandSubTab && type === 'MAIN_SUB' && !brandMainTab) ||
    (!brandSubTab && type === 'MAIN_SUB')
  ) {
    return null;
  }
  return (
    <BrandWrapSection2
      brandMainTitle={title}
      brandMainSubTitle={subTitle}
      startDate={brandMainTab?.displayStartDate}
      endDate={brandMainTab?.displayEndDate}
      serverTime={getAdjustedTime()}
      tabMenuList={getTabMenuList()}
      selectedTabId={brandMainTab?.scheduleId.toString()}
      categoryTabList={getMainBrandTabList()}
      selectedCateTabId={activeMainBrand?.brandCd?.toString()}
      brandMain={
        activeMainBrand && (type === 'MAIN_SUB' || type === 'MAIN')
          ? getBrandMainRender()
          : undefined
      }
      brandSub={
        brandSubTab && (type === 'MAIN_SUB' || type === 'SUB')
          ? getBrandSubRender()
          : undefined
      }
      colorType={sectionColorType}
      isDesktop={isDesktop}
    />
  );
}

export default memo(BrandModule);
